import type { dash } from "@dropbox/api-v2-client";
import type { ToolCall } from "@mirage/service-conversation/types";
import type { SearchResult } from "@mirage/service-dbx-api";
import type { TabInfo } from "@mirage/service-extension-tab-ingestion/service/types";
import type { StackItemShortcutWithMetadata } from "@mirage/service-stacks/service/types";
import type { Recommendation } from "@mirage/shared/search/recommendation";
import type { SummarizableResult } from "../SummaryQnaPanel/utils";

export type ChatConversationMessage =
  | ChatConversationMessageMessage
  | ChatConversationMessageInstruction
  | ChatConversationMessageToolCall;

export interface ChatConversationMessageMessage {
  type: "message";
  role: "user" | "assistant" | "system";
  text: string;
  json?: ChatResponse; // optional JSON content for structured data
  agentId?: string; // ID of the agent that generated this message
  referencingSources?: ChatSource[]; // sources directly cited in the message
  additionalContext?: ChatSource[]; // contextual sources considered but may not be directly cited
  actionContext?: ChatConversationMessageActionContext;
  followUpSuggestions?: string[];
  toolCalls?: ToolCall[];
}

export interface ChatConversationMessageInstruction {
  type: "instruction";
  role: "assistant";
  title: string;
  subtitle?: string;
}

/**
 * Responses to tool calls, these are often not shown to the user.
 */
export interface ChatConversationMessageToolCall {
  type: "tool_call";
  role: "tool";
  toolCallId: string;
  name: string;
  content: string;
}

// Base interface for JSON responses
interface ChatJsonResponse {
  type: string;
}

// Calendar-specific response type
export interface CalendarChatJsonResponse extends ChatJsonResponse {
  type: "calendar";
  calendarEvents: unknown;
}

// Union of all supported response types
export type ChatResponse = CalendarChatJsonResponse;

export type ChatConversationMessageActionContext =
  | {
      type: "reading";
    }
  | {
      type: "done"; // AI response completed (e.g. provided final answer)
    }
  | {
      type: "draft_generated";
    }
  | {
      type: "error";
    }
  | {
      type: "dash_search";
      queries: string[];
    }
  | {
      type: "dash_search_analyze";
    }
  | {
      type: "compose_selection_edit";
      selectedText: string;
      actionType?: "rewrite" | "make-longer" | "make-shorter"; // undefined if it is a custom prompt
    }
  | {
      type: "read_current_browser_tab";
      sourceTitle: string;
    }
  | {
      type: "template_generated";
    }
  | {
      type: "template_updated";
    }
  | {
      type: "template_selected";
    }
  | {
      type: "tone_generated";
    }
  | {
      type: "tone_updated";
    }
  | {
      type: "tone_selected";
    };

export type ChatSource =
  | ChatSourceDashSearchResult
  | ChatSourceRecommendation
  | ChatSourceTransient
  | ChatSourceLocalFileContent
  | ChatSourceMirageSearchResult
  | ChatSourceStackItem
  | ChatSourceSummarizableResult
  | ChatSourceBrowserTabSnapshot
  | ChatSourceBrowserTabCache
  | ChatSourceFssFile
  | ChatSourceFssFolder;

export enum ChatSourceType {
  Context = "context",
  Citation = "citation",
  None = "none",
}

export type ChatSourceInclusionType =
  | "suggestion"
  | "scope"
  | "summary"
  | "manual";

export interface ChatSourceBase {
  index?: string;
  inclusionType?: ChatSourceInclusionType;
}

export interface ChatSourceDashSearchResult extends ChatSourceBase {
  type: "dash_search_result";
  searchResult: dash.query_result_unionSearchResult;
}

export interface ChatSourceMirageSearchResult extends ChatSourceBase {
  type: "mirage_search_result";
  searchResult: SearchResult;
}

export interface ChatSourceRecommendation extends ChatSourceBase {
  type: "recommendation";
  recommendation: Recommendation;
}

export interface ChatSourceTransient extends ChatSourceBase {
  type: "transient";
  uuid: string;
  url: string;
  title: string;
}

export interface ChatSourceLocalFileContent extends ChatSourceBase {
  type: "local_file_content";
  uuid: string;
  fileName: string;
  content: string;
  fileSize?: number;
  mimeType?: string;
  url?: string; // usually not set for local_file_content, but set for assist demo flow
}

export interface ChatSourceStackItem extends ChatSourceBase {
  type: "stack_item";
  stackItem: StackItemShortcutWithMetadata;
}

export interface ChatSourceSummarizableResult extends ChatSourceBase {
  type: "summarizable_result";
  summarizableResult: SummarizableResult;
}

export interface ChatSourceBrowserTabSnapshot extends ChatSourceBase {
  type: "browser_tab_snapshot";
  tabInfo: TabInfo;
  body: string;
}

export interface ChatSourceBrowserTabCache extends ChatSourceBase {
  type: "browser_tab_cache";
  tabInfo: Pick<TabInfo, "url" | "title">;
  body: string;
}

export interface ChatSourceFssFile extends ChatSourceBase {
  type: "fss_file";
  fileId: string;
  name: string;
  url: string | null;
  extension?: string;
}

export interface ChatSourceFssFolder extends ChatSourceBase {
  type: "fss_folder";
  fileId: string;
  path: string;
  name?: string;
}
